/**
 * @file useScanFieldFocus.ts
 * @description Фокус поля скана (выделено из `ScanField` в phase19.22 шаг 5):
 *   автофокус при монтировании, возврат фокуса после запроса и правило
 *   камеры — после скана камерой на телефоне клавиатуру не поднимаем.
 *
 * Почему правило камеры живёт здесь: экран после скана ставит `busy`, а по
 * его окончании поле само возвращает себе фокус — на телефоне это всплывшая
 * клавиатура поверх списка. Поэтому «держим клавиатуру опущенной», пока
 * кладовщик сам не коснётся поля или не начнёт ввод.
 */

import { useCallback, useEffect, useRef, type RefObject } from "react";

import { isFocusTrapOpen } from "./scan-focus";
import { isCoarsePointer } from "./useCameraAvailable";

interface Params {
  inputRef: RefObject<HTMLInputElement | null>;
  busy: boolean;
  disabled: boolean;
  autoFocus: boolean;
}

export function useScanFieldFocus({ inputRef, busy, disabled, autoFocus }: Params) {
  /** Прошлые значения — фокус возвращаем только на ПЕРЕХОДЕ «занято → свободно». */
  const prev = useRef<{ busy: boolean; disabled: boolean; autoFocus: boolean } | null>(null);
  const keyboardDown = useRef(false);

  // Автофокус при монтировании (кроме `autoFocus={false}`) и возврат фокуса
  // после запроса. Открытая модалка держит свой focus-trap — её не трогаем.
  // phase19.31, О4: сравнение с прошлыми значениями, а не флаг «смонтирован»:
  // повторный прогон эффекта в StrictMode (dev) с теми же значениями раньше
  // считался «не первым» и ставил фокус полю с `autoFocus={false}`.
  useEffect(() => {
    const before = prev.current;
    prev.current = { busy, disabled, autoFocus };
    const wanted = before
      ? (before.busy && !busy) || (before.disabled && !disabled) || (!before.autoFocus && autoFocus)
      : autoFocus;
    if (!wanted || busy || disabled || isFocusTrapOpen() || keyboardDown.current) return;
    inputRef.current?.focus();
  }, [busy, disabled, autoFocus, inputRef]);

  /** Код пришёл с камеры: на десктопе вернуть фокус, на телефоне — нет. */
  const afterCameraScan = useCallback(() => {
    keyboardDown.current = isCoarsePointer();
    // После закрытия диалога base-ui возвращает фокус на кнопку — встаём после него.
    if (!keyboardDown.current) setTimeout(() => inputRef.current?.focus(), 0);
  }, [inputRef]);

  /** Кладовщик сам взялся за поле — обычные правила фокуса снова действуют. */
  const release = useCallback(() => {
    keyboardDown.current = false;
  }, []);

  return { afterCameraScan, release };
}
